import {
  createApi,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { waServicesQuery } from './baseQueries';

export interface TeamMemberAdvisorGroup {
  AdvisorGroupId: number;
  Name: string;
  TeamMemberMapId: number;
  IsAdministrator: boolean;
}

export interface TeamMemberAdvisor {
  AdvisorId: number;
  Name: string;
  TeamMemberMapId: number;
}

export interface TeamMember {
  UserId: number;
  Name: string;
  FirstName: string;
  LastName: string;
  UserName: string;
  IsActive: boolean;
  IsAdminLocked: boolean;
  IsAdmin: boolean;
  IsAdvisor: boolean;
  AdvisorId: number | null;
  AdvisorGroups: TeamMemberAdvisorGroup[];
  Advisors: TeamMemberAdvisor[];
  ExternalId: string | null;
  ExternalUserId: string | null;
  ShowIdFields: boolean;
}

export interface CreateTeamMemberRequest {
  FirstName: string;
  LastName: string;
  UserName: string;
  SendEmailInvite: boolean;
}

export interface LinkTeamMemberRequest {
  UserId: number;
  FirstName: string;
  LastName: string;
  UserName: string;
  AddAdvisor: true;
  SendEmailInvite: false;
}

export interface UpdateTeamMemberRequest {
  UserId: number;
  FirstName: string;
  LastName: string;
  UserName: string;
  ExternalId: string | null;
  ExternalUserId: string | null;
  AddAdvisor: false;
  SendEmailInvite: false;
}

export interface InviteTeamMemberRequest {
  UserId: number;
  FirstName: string;
  LastName: string;
  UserName: string;
  AddAdvisor: false;
  SendEmailInvite: true;
}

export interface TeamMembersResponse {
  Records: TeamMember[];
  TotalLength: number;
}

const putTeamMember = (body: { UserId: number }) => ({
  url: `/api/advisor/TeamMembers/${String(body.UserId)}`,
  method: 'PUT',
  body,
});

const refetchBothListsOnSuccess = (
  _result: TeamMember | boolean | undefined,
  error: FetchBaseQueryError | undefined,
) =>
  error
    ? []
    : [
        { type: 'TeamMembers' as const, id: 'LIST' },
        { type: 'TeamMembers' as const, id: 'AVAILABLE' },
      ];

export const teamMembersApi = createApi({
  reducerPath: 'teamMembersApi',
  baseQuery: waServicesQuery,
  tagTypes: ['TeamMembers'],
  endpoints: (builder) => ({
    getTeamMembers: builder.query<TeamMembersResponse, undefined>({
      query: () => ({
        url: '/api/advisor/TeamMembers',
        method: 'GET',
      }),
      providesTags: [{ type: 'TeamMembers', id: 'LIST' }],
    }),
    createTeamMember: builder.mutation<TeamMember, CreateTeamMemberRequest>({
      query: (body) => ({
        url: '/api/advisor/TeamMembers',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, error) =>
        error ? [] : [{ type: 'TeamMembers', id: 'LIST' }],
    }),
    getAvailableTeamMembers: builder.query<TeamMembersResponse, undefined>({
      query: () => ({
        url: '/api/advisor/AvailableTeamMembers',
        method: 'GET',
      }),
      providesTags: [{ type: 'TeamMembers', id: 'AVAILABLE' }],
    }),
    linkTeamMember: builder.mutation<TeamMember, LinkTeamMemberRequest>({
      query: putTeamMember,
      invalidatesTags: refetchBothListsOnSuccess,
    }),
    updateTeamMember: builder.mutation<TeamMember, UpdateTeamMemberRequest>({
      query: putTeamMember,
      async onQueryStarted(_request, { dispatch, queryFulfilled }) {
        const savedMember = await queryFulfilled.then(
          ({ data }) => data as TeamMember | null,
          () => null,
        );
        if (!savedMember) return;
        dispatch(
          teamMembersApi.util.updateQueryData(
            'getTeamMembers',
            undefined,
            (draft) => {
              const index = draft.Records.findIndex(
                (record) => record.UserId === savedMember.UserId,
              );
              if (index !== -1) draft.Records[index] = savedMember;
            },
          ),
        );
      },
      invalidatesTags: refetchBothListsOnSuccess,
    }),
    inviteTeamMember: builder.mutation<TeamMember, InviteTeamMemberRequest>({
      query: putTeamMember,
      invalidatesTags: refetchBothListsOnSuccess,
    }),
    removeTeamMember: builder.mutation<boolean, number>({
      query: (userId) => ({
        url: `/api/advisor/TeamMembers/${String(userId)}`,
        method: 'DELETE',
      }),
      invalidatesTags: refetchBothListsOnSuccess,
    }),
  }),
});

export const {
  useGetTeamMembersQuery,
  useCreateTeamMemberMutation,
  useGetAvailableTeamMembersQuery,
  useLinkTeamMemberMutation,
  useUpdateTeamMemberMutation,
  useInviteTeamMemberMutation,
  useRemoveTeamMemberMutation,
} = teamMembersApi;
